/**
 * The Finance handoff work screens (2026-10-10; AUTHORITY RULING, Isaiah Cotto:
 * the Finance handoff and its workflow state are EMERGE_OWNED).
 *
 * Accept, Record invoice, Record terms, Record payment state, Close, Return to
 * the Patient Advocate (private pay) and Verify liability, against master's
 * routes (financeWorkspace.js, the handoff acts). Receive is on the "Handed to
 * Finance" list (FinanceShell), where the UMED obligation's id is known. Each act:
 *
 *   - is offered only when the server says the caller may (`scope.can`, the
 *     capability AND the FINANCE_NATIVE_WRITES group) and the handoff's state
 *     allows it (forward-only, #358); the server decides again;
 *   - opens a hand-built drawer;
 *   - carries one Idempotency-Key per opening, reused only if the same body is
 *     retried, and the `version` the screen showed (expectedVersion);
 *   - on 409 STALE closes, re-reads the dossier and says someone else changed it
 *     first; on any other refusal says why and keeps the form; when the server
 *     could not be reached it says the act may or may not have been recorded.
 *
 * A private-pay handoff (#369) has no UMED: it shows its sale, and its way back
 * is the Patient Advocate. A corrected handoff (#366) carries amendments: Record
 * terms starts from the latest amended terms and says so.
 *
 * Ruling R1 (Isaiah Cotto, 2026-10-11): a UMED handoff whose case is linked to a
 * Monday Finance row carries that row as a REFERENCE, shown above the record and
 * again in Record invoice: "A Monday Finance row exists for this resident
 * (reference: ...) — check it before invoicing." When Monday already invoiced it,
 * "Already invoiced in Monday" records that (a close with a reason) instead of a
 * second invoice. Offered only on an ACCEPTED UMED handoff whose reference Finance
 * holds (attached when it was received); the server decides again.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { Drawer } from '../admin/bd/bdUi';
import { StateBlock } from '../../components/tide';
import {
  financeApi, outcomeOf, money, shortDate, freshKey,
  HANDOFF_STATE_LABEL, PAYMENT_STATE_LABEL,
  ApiResult, FinanceScope, ResidentContext, HandoffAct, HandoffState, PaymentState, saleTermsLine, PRIVATE_PAY_LABEL, payerLine,
} from './financeApi';

type HandoffRecord = NonNullable<ResidentContext['handoff']>;
/** What Record terms starts from, and why: the sale (private pay) or the latest amendment (a corrected handoff). Never binding. */
type TermsPrefill = { source: 'SALE' | 'AMENDMENT'; term_months: number; monthly_cents: number; total_cents: number; rule_version: string | null; basis: string };

type Act = 'accept' | 'invoice' | 'terms' | 'payment' | 'close' | 'invoiced_monday' | 'return' | 'verify';
export type HandoffWorkAct = Act;

/**
 * Open an act from outside (the Finance Kanban's card panel, 2026-10-14): a drop or "Move…" names the acts its move
 * may open. One act that is offered now opens its drawer -- its form, its confirmation, nothing recorded until the
 * button in it is pressed; several are offered as a choice; an act not offered now opens nothing. A fresh `n` per ask.
 */
export interface HandoffWorkRequest { acts: Act[]; n: number; payState?: Exclude<PaymentState, 'NONE'>; prompt?: string }

export const ACT_TITLE: Record<Act, string> = {
  accept: 'Accept handoff', invoice: 'Record invoice', terms: 'Record terms',
  payment: 'Record payment state', close: 'Close handoff', invoiced_monday: 'Already invoiced in Monday',
  return: 'Return to the Patient Advocate', verify: 'Verify liability',
};
const STALE_MESSAGE = 'Someone else changed this first. The current state is shown now; check it and try again.';
/** No answer (or a gateway's) is not a refusal: the write may have committed. The key makes the retry safe. */
export const UNSURE_MESSAGE = 'Could not reach the server; it may or may not have been recorded. Try again: the same submission will not be recorded twice.';
/** The heading over each kind of answer: a refusal was not recorded; no answer is not known either way. */
export const UNSURE_HEADING = 'Not sure it was recorded';
const REFUSED_HEADING = 'Not recorded';
type FormError = { message: string; status?: number; heading: string };

/**
 * Which acts are offered, by the handoff's state (financeHandoffService.TRANSITIONS; forward-only, #358). The server
 * decides again. Return is offered only for a private-pay handoff (RECEIVED or ACCEPTED): a UMED handoff goes back
 * through the signed send-back, which the dossier already offers.
 */
export function actsFor(h: HandoffRecord | null | undefined): Act[] {
  if (!h) return [];
  const out: Act[] = [];
  const s: HandoffState = h.state as HandoffState;
  if (s === 'RECEIVED') out.push('accept');
  if (s === 'ACCEPTED' || s === 'TERMS_RECORDED') out.push('invoice');
  if (s === 'ACCEPTED' || s === 'INVOICED') out.push('terms');
  if (s === 'COLLECTING') out.push('payment');
  if (s === 'PAID_IN_FULL' || s === 'ACCEPTED' || (s === 'COLLECTING' && h.payment_state === 'WRITTEN_OFF')) out.push('close');
  // R1: only before any native invoice, and only on the reference Finance holds (attached when it received the handoff).
  if (s === 'ACCEPTED' && h.kind !== 'PRIVATE_PAY' && h.monday_reference && h.monday_reference.source === 'ATTACHED') out.push('invoiced_monday');
  if (h.kind === 'PRIVATE_PAY' && (s === 'RECEIVED' || s === 'ACCEPTED')) out.push('return');
  return out;
}

/**
 * Terms to start from: for a private-pay handoff the sale as sold (never an amendment: the resident's older UMED
 * amendments are not this handoff's); for a UMED handoff the latest amendment of THIS handoff (its umed_action_id)
 * that carries amended terms; else nothing.
 */
export function prefillFor(ctx: ResidentContext): TermsPrefill | null {
  const h = ctx.handoff;
  if (!h) return null;
  if (h.kind === 'PRIVATE_PAY') {
    const sale = h.sale;
    if (sale && sale.term_months !== null && sale.monthly_cents !== null && sale.total_cents !== null) {
      return { source: 'SALE', term_months: sale.term_months, monthly_cents: sale.monthly_cents, total_cents: sale.total_cents, rule_version: sale.rule_version, basis: `As sold: ${saleTermsLine(sale)}.` };
    }
    return null;
  }
  // Only the NEWEST amendment of this handoff (amendments arrive newest first). If its terms are unknown, nothing is
  // prefilled: an older amendment's amounts would be a guess (verification of #377).
  const am = h.umed_action_id ? (ctx.amendments || []).find((a) => a.handoff_action_id === h.umed_action_id) : undefined;
  const known = Boolean(am && am.terms && am.terms.after && am.terms.after.months !== null && am.terms.after.monthlyCents !== null && am.terms.after.totalCents !== null);
  if (am && known && am.terms && am.terms.after) {
    const t = am.terms.after;
    return { source: 'AMENDMENT', term_months: t.months as number, monthly_cents: t.monthlyCents as number, total_cents: t.totalCents as number, rule_version: null,
      basis: `The handoff was corrected ${shortDate(am.corrected_on)} (${am.summary}).${am.terms.changed ? ' Its terms changed; these are the amended ones.' : ''}` };
  }
  return null;
}

/** A timestamp's day as Florida (Eastern) reads it; shortDate would take the UTC date prefix and run a day ahead after ~8 pm. */
export function easternDay(ts: string | null | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' });
}

const centsFrom = (dollars: string): number | null => {
  const t = dollars.trim().replace(/[$,]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
};
const dollarsOf = (cents: number | null | undefined): string => (cents === null || cents === undefined ? '' : (cents / 100).toFixed(2));

/** The drawer's body: a form that submits once per click with this opening's key, and says what happened. */
function ActForm({ label, submitLabel, busyLabel, onSubmit, children, disabled, err }: {
  label: string; submitLabel: string; busyLabel: string; onSubmit: () => Promise<void>; children?: React.ReactNode; disabled?: boolean; err: FormError | null;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <form className="um-form" aria-label={label} style={{ margin: 16 }} onSubmit={async (e) => {
      e.preventDefault();
      if (busy) return;
      setBusy(true);
      try { await onSubmit(); } finally { setBusy(false); }
    }}>
      {children}
      <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || disabled}>{busy ? busyLabel : submitLabel}</button></div>
      {err && <StateBlock kind="error" heading={err.heading} status={err.status} message={err.message} />}
    </form>
  );
}

/* Every input below is named by the visible text of the <label> that wraps it (WCAG 2.5.3, label in name): no aria-label. */

function TermsFields({ prefill, months, setMonths, monthly, setMonthly, total, setTotal }: {
  prefill: TermsPrefill | null; months: string; setMonths: (v: string) => void; monthly: string; setMonthly: (v: string) => void; total: string; setTotal: (v: string) => void;
}) {
  return (
    <>
      {prefill && <p className="um-quiet" role="note">Prefilled from {prefill.source === 'AMENDMENT' ? 'the latest amendment' : 'the private-pay sale'}. {prefill.basis} Change anything that is not right.</p>}
      {!prefill && <p className="um-quiet" role="note">Nothing to prefill: enter the terms Finance agreed.</p>}
      <label><span>Term in months</span><input inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value)} required /></label>
      <label><span>Monthly amount ($)</span><input inputMode="decimal" value={monthly} onChange={(e) => setMonthly(e.target.value)} required /></label>
      <label><span>Total ($)</span><input inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} required /></label>
      <p className="um-quiet">The term times the monthly must be within a dollar of the total.</p>
    </>
  );
}

/**
 * The handoff section of the dossier: Finance's own record (invoice, terms, payment state) and the acts the caller
 * may take on it now. The state line, the sale and the amendments are the dossier's own, above and below.
 */
export default function HandoffWork({ ctx, scope, onChanged, onReload, request = null }: {
  ctx: ResidentContext; scope: FinanceScope; onChanged: (msg: string) => Promise<void>; onReload: () => Promise<void>;
  /** An act asked for from outside (the Kanban's card panel). Never a write: at most it opens the act's drawer. */
  request?: HandoffWorkRequest | null;
}) {
  const h = ctx.handoff || null;
  const pp = Boolean(h && h.kind === 'PRIVATE_PAY');
  const canHandoff = Boolean(scope.can.manage_handoff);
  const canVerify = Boolean(scope.can.verify_liability);
  const prefill = prefillFor(ctx);

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const headingId = useId();
  const [open, setOpen] = useState<Act | null>(null);
  const [key, setKey] = useState<string>('');
  const [err, setErr] = useState<FormError | null>(null);
  /** The body this opening's key was last sent with. The server replays on the key alone, so a changed body MUST get a fresh key (keyFor does); a retry of the same body reuses it. */
  const sent = useRef<{ key: string; body: string } | null>(null);
  const [stale, setStale] = useState<string | null>(null);
  const [invoiceRef, setInvoiceRef] = useState('');
  const [invoiceOn, setInvoiceOn] = useState('');
  const [months, setMonths] = useState('');
  const [monthly, setMonthly] = useState('');
  const [total, setTotal] = useState('');
  const [payState, setPayState] = useState<Exclude<PaymentState, 'NONE'>>('PARTIAL');
  const [paidOn, setPaidOn] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [verifySource, setVerifySource] = useState<'FINANCE_VERIFIED' | 'FACILITY_STATEMENT'>('FINANCE_VERIFIED');
  const [sourceRef, setSourceRef] = useState('');

  const [choice, setChoice] = useState<{ acts: Act[]; prompt: string } | null>(null);
  const begin = (act: Act) => {
    setChoice(null);
    setErr(null); setStale(null); setKey(freshKey()); sent.current = null;
    setInvoiceRef(''); setInvoiceOn(''); setNote(''); setReason(''); setPaidOn(''); setPayState('PARTIAL'); setSourceRef('');
    if (act === 'terms') { setMonths(prefill ? String(prefill.term_months) : ''); setMonthly(prefill ? dollarsOf(prefill.monthly_cents) : ''); setTotal(prefill ? dollarsOf(prefill.total_cents) : ''); }
    if (act === 'verify') {
      // Never prefilled: ctx.liability.value is the Monday mirror, not Finance's verified fact. Finance enters what it verified.
      setAmount('');
      setEffectiveFrom(''); setVerifySource('FINANCE_VERIFIED');
    }
    setOpen(act);
  };
  const close = () => { setOpen(null); setErr(null); };
  /** After an act the trigger button usually no longer exists: put focus on the section, not on <body>. */
  const focusSection = () => { if (headingRef.current) headingRef.current.focus(); };

  /** One answer, handled the same way for every act. */
  const settle = async (r: ApiResult<{ replayed?: boolean }>, done: string, again: string) => {
    if (r.success && r.data) {
      setOpen(null);
      await onChanged(r.data.replayed ? again : done);
      focusSection();
      return;
    }
    if (r.code === 'STALE') {
      setOpen(null);
      setStale(STALE_MESSAGE);
      await onReload();
      focusSection();
      return;
    }
    if (r.status === undefined || r.status === 502 || r.status === 503 || r.status === 504) {
      setErr({ message: UNSURE_MESSAGE, status: r.status, heading: UNSURE_HEADING });
      return;
    }
    setErr({ message: outcomeOf(r, { wrote: true }), status: r.status, heading: REFUSED_HEADING });
  };
  const run = async (call: () => Promise<ApiResult<{ replayed?: boolean }>>, done: string, again = 'That was already recorded.') => {
    setErr(null);
    try { await settle(await call(), done, again); } catch { setErr({ message: UNSURE_MESSAGE, heading: UNSURE_HEADING }); }
  };
  /**
   * The key for this submission. Retrying the same body reuses the opening's key, so an unsure first attempt is never
   * recorded twice. The server replays on the key alone (it does not compare bodies), so a body changed after an
   * answer must get a new key or it would be answered with the first act's result; correctness rests on this, and the
   * version the screen showed guards a second act against a first that did commit.
   */
  const keyFor = (body: unknown): string => {
    const fp = JSON.stringify(body);
    if (!sent.current) sent.current = { key, body: fp };
    else if (sent.current.body !== fp) { const next = freshKey(); setKey(next); sent.current = { key: next, body: fp }; }
    return sent.current.key;
  };
  const act = (a: HandoffAct, body: Record<string, unknown>) => financeApi.handoffAct((h as HandoffRecord).id, a, { ...body, version: (h as HandoffRecord).version }, keyFor({ act: a, ...body }));

  const termsCents = { months: /^\d+$/.test(months.trim()) ? Number(months.trim()) : null, monthly: centsFrom(monthly), total: centsFrom(total) };
  const termsValid = termsCents.months !== null && termsCents.months >= 1 && termsCents.monthly !== null && termsCents.total !== null;
  // The rule version travels only when the amounts are exactly the sale's, as sold; anything else is entered by hand.
  const termsFromSale = Boolean(prefill && prefill.source === 'SALE' && termsCents.months === prefill.term_months && termsCents.monthly === prefill.monthly_cents && termsCents.total === prefill.total_cents);
  const needsReason = Boolean(h && h.state !== 'PAID_IN_FULL');
  const acts: Act[] = canHandoff ? actsFor(h) : [];

  // An act asked for from outside: open it if it is offered now (the state the screen shows), else nothing.
  const seenRequest = useRef<number | null>(null);
  useEffect(() => {
    if (!request || seenRequest.current === request.n) return;
    seenRequest.current = request.n;
    const offered = request.acts.filter((a) => (a === 'verify' ? canVerify : acts.includes(a)));
    // F7: a drawer already open keeps what was typed in it. The same act asked again leaves it as it is; another act
    // is offered beside it, never opened over it.
    if (open !== null) {
      if (offered.includes(open)) return;
      if (offered.length) setChoice({ acts: offered, prompt: `${ACT_TITLE[open]} is open with what you typed in it. Finish or close it before opening another act.` });
      return;
    }
    if (offered.length === 1) {
      begin(offered[0]);
      if (offered[0] === 'payment' && request.payState) setPayState(request.payState);
    } else if (offered.length > 1) {
      setChoice({ acts: offered, prompt: request.prompt || 'Choose the act to open.' });
    } else {
      setChoice(null);
    }
  // begin and acts are this render's; the request's n is what decides whether to act.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  if (!h && !canVerify) return null;

  return (
    <section className="um-section" aria-labelledby={headingId}>
      <h3 id={headingId} ref={headingRef} tabIndex={-1}>{pp ? `Finance handoff work · ${PRIVATE_PAY_LABEL}` : 'Finance handoff work'}</h3>
      {stale && <p className="um-notice stop" role="alert">{stale}</p>}

      {h && (
        <div role="group" aria-label="Finance's record of the handoff">
          {h.monday_reference && (
            <p className={h.monday_reference.points_to === 'THIS_RESIDENT' ? 'um-notice' : 'um-notice stop'} role="note" aria-label="Monday Finance reference">
              {h.monday_reference.notice}
            </p>
          )}
          {/* 2026-10-15: the resident paid before DCF approved -- read from the UMED case's mark, never a payment state. */}
          {h.reimbursement_note && <p className="um-notice" role="note" data-testid="handoff-reimbursement-note">{h.reimbursement_note}{h.paid_in_full?.reason ? ` (${h.paid_in_full.reason})` : ''}.</p>}
          <dl className="um-kv">
            {pp && <><dt>Paid by</dt><dd className={h.sale?.payer ? '' : 'none'} data-testid="pp-payer">{payerLine(h.sale?.payer)}</dd></>}
            <dt>Payment</dt><dd>{PAYMENT_STATE_LABEL[(h.payment_state || 'NONE') as PaymentState] || h.payment_state}</dd>
            <dt>Accepted by</dt><dd className={h.accepted_by_name ? '' : 'none'}>{h.accepted_by_name ? `${h.accepted_by_name}${h.accepted_at ? ` · ${easternDay(h.accepted_at)}` : ''}` : 'Not recorded'}</dd>
            <dt>Invoice</dt><dd className={h.invoice ? '' : 'none'}>{h.invoice ? `${h.invoice.reference} · ${shortDate(h.invoice.created_on)}` : 'Not recorded'}</dd>
            <dt>Terms</dt><dd className={h.terms ? '' : 'none'}>{h.terms ? `${h.terms.term_months} × ${money(h.terms.monthly_cents / 100)} = ${money(h.terms.total_cents / 100)}${h.terms.rule_version ? ` (${h.terms.rule_version})` : ' (entered by hand)'}` : 'Not recorded'}</dd>
            {h.close_reason && <><dt>Reason</dt><dd>{h.close_reason}{h.closed_by_name ? ` · ${h.closed_by_name}` : ''}</dd></>}
          </dl>
          {choice && (
            <div className="um-notice" role="group" aria-label="Choose the act">
              <p style={{ margin: '0 0 8px' }}>{choice.prompt}</p>
              <div className="btns" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {choice.acts.map((a) => <button key={a} type="button" className="um-open um-primary" onClick={() => begin(a)}>{ACT_TITLE[a]}…</button>)}
              </div>
            </div>
          )}
          {acts.length > 0 && (
            <div className="btns" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }} role="group" aria-label="Handoff actions">
              {acts.map((a) => <button key={a} type="button" className="um-open" onClick={() => begin(a)}>{ACT_TITLE[a]}…</button>)}
            </div>
          )}
        </div>
      )}

      {canVerify && (
        <div className="btns" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }} role="group" aria-label="Liability actions">
          <button type="button" className="um-open" onClick={() => begin('verify')}>{ACT_TITLE.verify}…</button>
        </div>
      )}

      <Drawer scope="um" open={open !== null} onClose={close} title={open ? ACT_TITLE[open] : ''} description={`${ctx.resident.name}${ctx.facility ? ` · ${ctx.facility.name}` : ''}`}>
        {open === 'accept' && h && (
          <ActForm label="Accept handoff" submitLabel="Accept" busyLabel="Accepting…" err={err}
            onSubmit={() => run(() => act('accept', {}), 'Handoff accepted. You hold it now.')}>
            <p>You take this handoff: you become its acceptor{pp ? '' : ', and only you can send it back'}.</p>
          </ActForm>
        )}
        {open === 'invoice' && h && (
          <ActForm label="Record invoice" submitLabel="Record invoice" busyLabel="Recording…" err={err} disabled={!invoiceRef.trim() || !invoiceOn}
            onSubmit={() => run(() => act('invoice', { invoice_reference: invoiceRef.trim(), invoice_created_on: invoiceOn }), 'Invoice recorded.')}>
            {h.monday_reference && <p className="um-notice" role="note">{h.monday_reference.notice}</p>}
            <label><span>Invoice reference</span><input value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} maxLength={200} required /></label>
            <label><span>Invoice created on</span><input type="date" value={invoiceOn} onChange={(e) => setInvoiceOn(e.target.value)} required /></label>
          </ActForm>
        )}
        {open === 'terms' && h && (
          <ActForm label="Record terms" submitLabel="Record terms" busyLabel="Recording…" err={err} disabled={!termsValid}
            onSubmit={() => run(() => act('terms', {
              term_months: termsCents.months, monthly_cents: termsCents.monthly, total_cents: termsCents.total,
              terms_rule_version: termsFromSale && prefill ? prefill.rule_version : null,
            }), 'Terms recorded.')}>
            <TermsFields prefill={prefill} months={months} setMonths={setMonths} monthly={monthly} setMonthly={setMonthly} total={total} setTotal={setTotal} />
          </ActForm>
        )}
        {open === 'payment' && h && (
          <ActForm label="Record payment state" submitLabel="Record payment state" busyLabel="Recording…" err={err}
            onSubmit={() => run(() => act('payment', { payment_state: payState, paid_on: paidOn || null, note: note.trim() || null }), 'Payment state recorded.')}>
            <label><span>What happened</span>
              <select value={payState} onChange={(e) => setPayState(e.target.value as Exclude<PaymentState, 'NONE'>)}>
                <option value="PARTIAL">{PAYMENT_STATE_LABEL.PARTIAL}</option>
                <option value="PAID_IN_FULL">{PAYMENT_STATE_LABEL.PAID_IN_FULL}</option>
                <option value="WRITTEN_OFF">{PAYMENT_STATE_LABEL.WRITTEN_OFF}</option>
              </select>
            </label>
            <label><span>Paid on (optional)</span><input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} /></label>
            <label><span>Note (minimum necessary)</span><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="No card, account, routing or Social Security numbers" /></label>
            <p className="um-quiet">This records what Finance saw happen, never an amount.</p>
          </ActForm>
        )}
        {open === 'close' && h && (
          <ActForm label="Close handoff" submitLabel="Close handoff" busyLabel="Closing…" err={err} disabled={needsReason && !reason.trim()}
            onSubmit={() => run(() => act('close', { reason: reason.trim() || null }), pp ? 'Handoff closed.' : "Handoff closed. UMED's handoff obligation is complete.")}>
            <p>{pp ? "Closing ends Finance's work on this private-pay handoff. There is no UMED obligation to complete." : "Closing completes UMED's handoff obligation in eMerge."}</p>
            {h.state === 'ACCEPTED' && <p className="um-notice stop" role="note">This closes the handoff without collection: it skips invoicing and terms, and nothing is invoiced in eMerge.</p>}
            <label><span>{needsReason ? 'Why (required)' : 'Note (optional)'}</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={3} required={needsReason} /></label>
            <p className="um-quiet">Minimum necessary: no card, account, routing or Social Security numbers.</p>
          </ActForm>
        )}
        {open === 'invoiced_monday' && h && h.monday_reference && (
          <ActForm label="Already invoiced in Monday" submitLabel="Record as invoiced in Monday" busyLabel="Recording…" err={err} disabled={!reason.trim()}
            onSubmit={() => run(() => act('invoiced-in-monday', { reason: reason.trim() }), "Recorded as already invoiced in Monday. The handoff is closed and UMED's handoff obligation is complete.")}>
            <p className="um-notice" role="note">{h.monday_reference.notice}</p>
            <p>You checked the Monday Finance row and Monday already invoiced this resident. eMerge records that instead of a second invoice: the handoff closes with your reason, and nothing is invoiced in eMerge.</p>
            <label><span>How it was invoiced in Monday (required)</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1900} rows={3} required /></label>
            <p className="um-quiet">Minimum necessary: no card, account, routing or Social Security numbers.</p>
          </ActForm>
        )}
        {open === 'return' && h && (
          <ActForm label="Return to the Patient Advocate" submitLabel="Return to the Patient Advocate" busyLabel="Returning…" err={err} disabled={!reason.trim()}
            onSubmit={() => run(() => act('return-to-pa', { reason: reason.trim() }), 'Returned to the Patient Advocate.')}>
            <p>Finance cannot work this private-pay handoff. It goes back to the Patient Advocate with your reason; the sale is untouched.</p>
            <label><span>Why (required)</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={3} required /></label>
          </ActForm>
        )}
        {open === 'verify' && (
          <ActForm label="Verify liability" submitLabel="Verify liability" busyLabel="Recording…" err={err} disabled={centsFrom(amount) === null}
            onSubmit={() => run(async () => {
              const body = {
                amount_cents: centsFrom(amount) as number, effective_from: effectiveFrom || undefined, verification_source: verifySource,
                source_reference: sourceRef.trim() || undefined, reason: reason.trim() || undefined,
              };
              const r = await financeApi.verifyLiability(ctx.resident.id, body, keyFor({ act: 'verify', ...body }));
              // The same statement again is not a new fact (changed: false): say so, as for a replay.
              return r.success && r.data && r.data.changed === false ? { ...r, data: { ...r.data, replayed: true } } : r;
            }, 'Liability verified.', 'That liability was already recorded as verified.')}>
            <p className="um-quiet">{ctx.liability.value !== null && ctx.liability.value !== undefined ? `Monday value (reference, not verified): ${money(ctx.liability.value)}. Enter the amount Finance verified.` : 'No Monday value is on file. Enter the amount Finance verified.'}</p>
            <label><span>Monthly liability ($)</span><input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required /></label>
            <label><span>Effective from (optional)</span><input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} /></label>
            <label><span>Verified from</span>
              <select value={verifySource} onChange={(e) => setVerifySource(e.target.value as 'FINANCE_VERIFIED' | 'FACILITY_STATEMENT')}>
                <option value="FINANCE_VERIFIED">Finance's own check</option>
                <option value="FACILITY_STATEMENT">The facility's statement</option>
              </select>
            </label>
            <label><span>Source reference (optional)</span><input value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} maxLength={200} /></label>
            <label><span>Reason (optional)</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} /></label>
          </ActForm>
        )}
      </Drawer>
    </section>
  );
}
